import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    const token = searchParams.get('token');
    const refreshToken = searchParams.get('refresh_token');
    if (!token) {
      setError(t('auth.loginError'));
      return;
    }
    hasProcessed.current = true;
    let cancelled = false;

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      setError(t('auth.loginError'));
      hasProcessed.current = false;
    }, 15000);

    loginWithToken(token, refreshToken ?? undefined)
      .then((success) => {
        window.clearTimeout(timeoutId);
        if (success) {
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('just_logged_in', 'true');
          }
          navigate('/dashboard', { replace: true });
          // Fallback: if still on callback after a tick (e.g. Strict Mode unmounted us), force redirect
          setTimeout(() => {
            if (window.location.pathname.includes('/auth/callback')) {
              window.location.replace('/dashboard');
            }
          }, 100);
        } else {
          if (!cancelled) {
            setError(t('auth.loginError'));
            hasProcessed.current = false;
          }
        }
      })
      .catch(() => {
        window.clearTimeout(timeoutId);
        if (!cancelled) {
          setError(t('auth.loginError'));
          hasProcessed.current = false;
        }
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchParams, loginWithToken, navigate, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg p-4">
        <div className="theme-text text-center">
          <p className="mb-4">{error}</p>
          <a href="/login" className="text-indigo-400 hover:text-indigo-300 font-medium">
            {t('auth.loginButton')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center theme-bg p-4">
      <div className="flex flex-col items-center gap-4 theme-text">
        <svg
          className="animate-spin w-8 h-8 text-indigo-400"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p>{t('auth.loggingIn')}</p>
      </div>
    </div>
  );
}
